// Bridges the service-worker updater from registerSW() (main.tsx) to the
// UpdateBanner.
//
// The hard problem this file solves: on iOS standalone PWAs the SW-update
// → page-reload handshake is fragile. The known failure modes:
//   1. A bare window.location.reload() is intercepted by the still-active
//      (stale) Workbox SW, which re-serves the old precached app shell
//      and the app never actually updates.
//   2. After skipWaiting + clientsClaim, the new SW takes over BEFORE its
//      precache has fully populated. A reload at that exact instant gets
//      the new index.html (which references new chunk hashes) but the
//      chunk fetches return navigation fallback HTML, the browser tries
//      to parse it as JS, fails silently, and the user is left staring
//      at a black timed-out screen — exactly what users have reported.
//   3. The SKIP_WAITING postMessage handshake sometimes never resolves
//      on iOS (especially if the page was a background tab).
//
// The pattern below is layered:
//   • Tell the waiting SW to skipWaiting + wait for controllerchange so we
//     reload after the new worker is actually in charge.
//   • Reload via location.replace() with a cache-busting ?_v= query so the
//     navigation request is a guaranteed cache miss at every tier (runtime
//     SW cache, HTTP cache, CDN). This dodges failure mode #2.
//   • If anything in that pipeline fails or times out, fall through to a
//     nuke-and-reload: unregister every SW + delete every Cache Storage
//     bucket + reload. Worst case we lose offline cache for one session,
//     never strand the user.

type Updater = (reloadPage?: boolean) => Promise<void>;

let updater: Updater | null = null;

export function setSWUpdater(fn: Updater) {
  updater = fn;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, fallback: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), timeoutMs)),
  ]);
}

function hardReload(): void {
  // Cache-bust the navigation URL so the SW (old or new) is forced to
  // serve a fresh request instead of a stale precached response.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    const nextUrl = url.toString();
    window.location.replace(nextUrl);
    // iOS standalone PWAs occasionally ignore replace() while recovering from
    // a SW/cache transition. Follow up with assign/reload if the page has not
    // begun unloading. These timers are harmless when replace() succeeds.
    setTimeout(() => {
      try { window.location.assign(nextUrl); } catch { window.location.reload(); }
    }, 1200);
    setTimeout(() => {
      try { window.location.reload(); } catch { /* nothing left to do */ }
    }, 3500);
  } catch {
    window.location.reload();
  }
}

async function clearServiceWorkersAndCaches(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await withTimeout(navigator.serviceWorker.getRegistrations(), 1200, []);
      await withTimeout(
        Promise.all(regs.map((r) => r.unregister().catch(() => false))),
        1800,
        [],
      );
    }
    if ("caches" in window) {
      const names = await withTimeout(caches.keys(), 1200, []);
      await withTimeout(
        Promise.all(names.map((n) => caches.delete(n).catch(() => false))),
        1800,
        [],
      );
    }
  } catch (e) {
    console.warn("[applyAppUpdate] cleanup failed, reloading anyway:", e);
  }
}

export async function nukeAndReload(): Promise<void> {
  await clearServiceWorkersAndCaches();
  hardReload();
}

export async function applyAppUpdate(): Promise<void> {
  // Primary path is now nuke-and-reload: fully unregister the service worker
  // and purge every Cache Storage bucket BEFORE reloading. This is the only
  // approach that reliably avoids the black-screen-on-update on iOS standalone
  // PWAs (failure mode #2 above): the older skipWaiting + controllerchange
  // handshake "succeeds" mechanically but can still strand the user on a blank
  // screen when the freshly-activated SW serves the new index.html while its
  // precache hasn't populated the new chunk hashes — the chunk fetches return
  // fallback HTML, the browser fails to parse it as JS, and nothing renders.
  // Because that path doesn't throw, the old nuke fallback never fired.
  //
  // With no SW and no caches, the reload's navigation AND every chunk fetch go
  // straight to the network — guaranteed fresh. The SW re-registers cleanly on
  // the next load and re-precaches in the background. Cost is ~nil: after a
  // deploy the new hashed chunks aren't in any cache anyway. The update prompt
  // only appears when /api/version polling succeeded, so the device is online.
  //
  // First nudge the waiting SW to skipWaiting (best-effort, bounded) so push
  // continuity isn't interrupted longer than necessary, then nuke regardless.
  try {
    if (updater) {
      await Promise.race([
        updater(false).catch(() => {}),
        new Promise<void>((resolve) => setTimeout(resolve, 800)),
      ]);
    }
  } catch { /* best-effort — the nuke below is what actually fixes the page */ }
  await withTimeout(clearServiceWorkersAndCaches(), 4000, undefined);
  hardReload();
}
